import { useState } from 'react';
import { api, type Me } from '../api';
import { Alert } from '../components/Alert';
import { AuditTab } from '../components/AuditTab';
import { AssignmentsPanel } from '../components/instance/AssignmentsPanel';
import { ManifestPanel } from '../components/instance/ManifestPanel';
import { ReportPanel } from '../components/instance/ReportPanel';
import { TokenPanel } from '../components/instance/TokenPanel';
import { StatusBadge } from '../components/StatusBadge';
import { TabPanel, Tabs } from '../components/Tabs';
import { relativeTime } from '../format';
import { useInterval, useLoader, useNow } from '../hooks';
import { Link } from '../router';

type Tab = 'config' | 'audit';
const TABS: { id: Tab; label: string }[] = [
  { id: 'config', label: 'Configuration' },
  { id: 'audit', label: 'Audit' },
];

/** Délai entre deux relectures du rapport de l'agent (il en envoie un toutes les 5 minutes environ). */
const REPORT_REFRESH_MS = 30_000;

export function InstanceDetail({ me, instanceId }: { me: Me; instanceId: string }) {
  const [tab, setTab] = useState<Tab>('config');
  const users = useLoader(api.adminUsers);
  const preview = useLoader(() => api.manifestPreview(instanceId), instanceId);
  const report = useLoader(async () => ({ value: await api.instanceReport(instanceId) }), instanceId);
  const now = useNow(REPORT_REFRESH_MS);
  useInterval(() => void report.reload(), REPORT_REFRESH_MS);

  const found = users.data
    ?.flatMap((user) => user.instances.map((instance) => ({ user, instance })))
    .find((x) => x.instance.id === instanceId);

  const back = (
    <p>
      <Link href="/admin?tab=users">← Administration</Link>
    </p>
  );

  if (users.error) {
    return (
      <div className="stack">
        {back}
        <Alert>{users.error}</Alert>
      </div>
    );
  }
  if (!users.data) {
    return (
      <div className="stack">
        {back}
        <p className="muted" aria-busy="true">
          Chargement de l'instance…
        </p>
      </div>
    );
  }
  if (!found) {
    return (
      <div className="stack">
        {back}
        <Alert>Instance introuvable : elle a peut-être été retirée.</Alert>
      </div>
    );
  }

  const { user, instance } = found;
  const kindLabel = instance.kind === 'cloud' ? 'cloud' : 'locale';

  return (
    <div className="stack">
      {back}
      <div className="page-head">
        <h1>
          Instance {kindLabel} de {user.username}
        </h1>
        <StatusBadge status={instance.status} />
      </div>
      <p className="muted">
        Canal {instance.channel} · {instance.version ?? 'version inconnue'} · vue{' '}
        {relativeTime(instance.lastSeenAt)}
        {instance.url && (
          <>
            {' · '}
            <a href={instance.url} target="_blank" rel="noreferrer noopener" className="url">
              {instance.url}
            </a>
          </>
        )}
      </p>
      {instance.status === 'removed' && (
        <Alert kind="warning">Cette instance est retirée : sa configuration n'est plus servie.</Alert>
      )}

      <Tabs label="Sections de l'instance" tabs={TABS} active={tab} onChange={setTab} prefix="inst" />

      {tab === 'config' && (
        <TabPanel prefix="inst" id="config">
          <div className="stack">
            <section className="panel stack" aria-labelledby="sec-report">
              <h2 id="sec-report">Agent : dernier rapport</h2>
              {report.error && <Alert>{report.error}</Alert>}
              {report.data === null && !report.error && (
                <p className="muted" aria-busy="true">
                  Chargement du rapport…
                </p>
              )}
              {report.data && (
                <ReportPanel
                  report={report.data.value}
                  desiredRevision={preview.data?.manifest.revision ?? null}
                  manifestFlags={preview.data?.manifest.flags ?? null}
                  now={now}
                />
              )}
            </section>

            <section className="panel stack" aria-labelledby="sec-token">
              <h2 id="sec-token">Jeton d'agent</h2>
              <TokenPanel key={instanceId} instanceId={instanceId} kind={instance.kind} me={me} />
            </section>

            <section className="panel stack" aria-labelledby="sec-assign">
              <h2 id="sec-assign">Attributions</h2>
              <AssignmentsPanel instanceId={instanceId} onChanged={() => void preview.reload()} />
            </section>

            <section className="panel stack" aria-labelledby="sec-manifest">
              <h2 id="sec-manifest">Manifeste et dérive</h2>
              <ManifestPanel
                instanceId={instanceId}
                preview={preview.data}
                error={preview.error}
                onChanged={() => void preview.reload()}
              />
            </section>
          </div>
        </TabPanel>
      )}
      {tab === 'audit' && (
        <TabPanel prefix="inst" id="audit">
          <AuditTab fixedTarget={{ type: 'instance', id: instanceId }} />
        </TabPanel>
      )}
    </div>
  );
}
